import React, { useEffect, useState, useContext } from "react";
import { Avatar, Tabs, Image, Switch, Modal, Form, Input, Select, Button, Table, message } from "antd";
import {
  PercentageOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";

import { API_BASE_PATH } from "../../config";
import {
  DateFormat,
  getApi,
  saveApi,
  successMessage,
  errorMessage,
} from "../../helpers";
import CardContainer from "../../components/Card";
import TableContainer from "../../components/Table";
import OfferPopup from "./OfferPopup";
import TagPopup from "./TagPopup";
import OfferSidebar from "./OfferSidebar";
import { StoreContext } from "../../context";
import AdPopup from "./AdPopup";
import AdSidebar from "./AdSidebar";
import TVAdEditor from "./TVAdEditor";
import BackdropPopup from "./BackdropPopup";
import BackdropSidebar from "./BackdropSidebar";


const baseOfferColumns = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
  },
  {
    title: "Coupon Code",
    dataIndex: "coupon_code",
    key: "coupon_code",
    sorter: true,
  },
  {
    title: "Tag Name",
    dataIndex: "tag_name",
    key: "tag_name",
  },
  {
    title: "Valid From",
    dataIndex: "valid_from",
    key: "valid_from",
    align: "center",
    render: (text) => DateFormat(text, "DD MMM YY"),
  },
  {
    title: "Valid Till",
    dataIndex: "valid_till",
    key: "valid_till",
    align: "center",
    render: (text) => DateFormat(text, "DD MMM YY"),
  },
  {
    title: "Date",
    dataIndex: "created_at",
    key: "created_at",
    align: "center",
    render: (text) => DateFormat(text),
    sorter: true,
  },
  {
    title: "Image",
    dataIndex: "offer_image",
    key: "offer_image",
    align: "center",
    render: (text) => (
      <Avatar
        shape="square"
        icon={<PercentageOutlined />}
        size={50}
        alt=""
        src={text}
      />
    ),
  },
];

const tagColumns = [
  {
    title: "Name",
    dataIndex: "tag_name",
    key: "tag_name",
    sorter: true,
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    ellipsis: {
      showTitle: true,
    },
  },
  {
    title: "Date",
    dataIndex: "created_at",
    key: "created_at",
    align: "center",
    render: (text) => DateFormat(text),
    sorter: true,
  },
];

//offers - id,name,description,coupon_code,valid_from,valid_till,is_percent,percent_discount,amount_discount,product_id,product_name,category_id,category_name,tag_id,tag_name,created_at,updated_at,created_by,updated_by
export default function Offers() {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [activeKey, setActiveKey] = useState("1");
  const [storeMap, setStoreMap] = useState({});

  const storeColumn = {
    title: "Store Name",
    dataIndex: "store_id",
    key: "store_name",
    render: (text, record) =>
      record?.store_name || storeMap[text] || (text ? `Store ${text}` : "-"),
  };

  const offerColumns = storeId
    ? [...baseOfferColumns]
    : [
      ...baseOfferColumns.slice(0, 1),
      storeColumn,
      ...baseOfferColumns.slice(1),
    ];

  // Offers state
  const [offerPayload, setOfferPayload] = useState({});
  const [offerTotal, setOfferTotal] = useState(8);
  const [offerList, setOfferList] = useState([]);
  const [offerSelected, setOfferSelected] = useState({});
  const [offerSidebar, setOfferSidebar] = useState(false);
  const [offerPopup, setOfferPopup] = useState(false);
  const [offerPopupTitle, setOfferPopupTitle] = useState("");

  // Tags state
  const [tagPayload, setTagPayload] = useState({});
  const [tagTotal, setTagTotal] = useState(8);
  const [tagList, setTagList] = useState([]);
  const [tagSelected, setTagSelected] = useState({});
  const [tagPopup, setTagPopup] = useState(false);
  const [tagPopupTitle, setTagPopupTitle] = useState("");

  // Ads/Banners state
  const [adList, setAdList] = useState([]);
  const [adTotal, setAdTotal] = useState(0);
  const [adPayload, setAdPayload] = useState({});
  const [adSelected, setAdSelected] = useState({});
  const [adPopup, setAdPopup] = useState(false);
  const [adSidebar, setAdSidebar] = useState(false);

  // Backdrop state
  const [backdropList, setBackdropList] = useState([]);
  const [backdropTotal, setBackdropTotal] = useState(0);
  const [backdropPayload, setBackdropPayload] = useState({});
  const [backdropSelected, setBackdropSelected] = useState({});
  const [backdropPopup, setBackdropPopup] = useState(false);
  const [backdropSidebar, setBackdropSidebar] = useState(false);

  // Offer Assign state
  const [assignForm] = Form.useForm();
  const [assignLoading, setAssignLoading] = useState(false);
  const [showAssignedTable, setShowAssignedTable] = useState(false);
  const [assignedOfferList, setAssignedOfferList] = useState([]);
  const [assignedOfferLoading, setAssignedOfferLoading] = useState(false);
  const [selectedAssignedOffer, setSelectedAssignedOffer] = useState(null);

  // Move adColumns inside the component to access handleBannerStatusChange
  const adColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    ...(!storeId ? [storeColumn] : []),
    {
      title: "Banner Type",
      dataIndex: "banner_type",
      key: "banner_type",
      render: (text) => (
        <span style={{ textTransform: "capitalize" }}>
          {text || "homepage"}
        </span>
      ),
    },
    {
      title: "Action Type",
      dataIndex: "type",
      key: "type",
      render: (text) => (
        <span style={{ textTransform: "capitalize" }}>
          {text === "sub_category" ? "Sub Category" : text}
        </span>
      ),
      sorter: true,
    },
    {
      title: "Valid From",
      dataIndex: "valid_from",
      key: "valid_from",
      align: "center",
      render: (text) => DateFormat(text, "DD MMM YY"),
    },
    {
      title: "Valid Till",
      dataIndex: "valid_till",
      key: "valid_till",
      align: "center",
      render: (text) => DateFormat(text, "DD MMM YY"),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      render: (text) => DateFormat(text),
      sorter: true,
    },
    {
      title: "Image",
      dataIndex: "banner_image",
      key: "banner_image",
      align: "center",
      render: (text) => (
        <Image
          preview={false}
          width={100}
          alt=""
          src={text}
          fallback={<PercentageOutlined />}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      render: (isActive, record) => (
        <Switch
          checked={isActive === 1}
          onChange={(checked) => handleBannerStatusChange(record, checked)}
          size="small"
        />
      ),
    },
  ];

  // Add the status change handler with all banner details
  const handleBannerStatusChange = (record, checked) => {
    const statusText = checked ? "activate" : "deactivate";

    Modal.confirm({
      title: `Confirm Banner Status Change`,
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to ${statusText} the banner "${record.name}"?`,
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        setLoading(true);
        try {
          // Pass all banner details like in updateBanner API
          const values = {
            banner_id: record.banner_id,
            name: record.name,
            type: record.type,
            valid_from: DateFormat(record.valid_from, "YYYY-MM-DD HH:mm:ss"),
            valid_till: DateFormat(record.valid_till, "YYYY-MM-DD HH:mm:ss"),
            is_active: checked ? 1 : 0,
            store_id: record.store_id,
            // Include type-specific fields based on banner type
            ...(record.type === "category" && {
              category_id: record.category_id,
            }),
            ...(record.type === "sub_category" && {
              sub_category_id: record.sub_category_id,
              category_id: record.category_id,
            }),
            ...(record.type === "product" && {
              product_variant_id: record.product_variant_id,
            }),
            ...(record.type === "offer" && { offer_id: record.offer_id }),
            ...(record.type === "url" && { url: record.url }),
          };

          const res = await saveApi(values, "", "updateBanner");

          if (res) {
            successMessage(`Banner ${statusText}d successfully`);
            getAds(adPayload);
          } else {
            errorMessage(`Failed to ${statusText} banner`);
            setLoading(false);
          }
        } catch (error) {
          console.error("Error updating banner status:", error);
          errorMessage(`Failed to ${statusText} banner`);
          setLoading(false);
        }
      },
    });
  };

  // Backdrop columns
  const backdropColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    ...(!storeId ? [storeColumn] : []),
    {
      title: "Colour 1",
      dataIndex: "colour_1",
      key: "colour_1",
      align: "center",
      render: (color) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 4,
              backgroundColor: color,
              border: "1px solid #d9d9d9",
            }}
          ></div>
          <span>{color}</span>
        </div>
      ),
    },
    {
      title: "Colour 2",
      dataIndex: "colour_2",
      key: "colour_2",
      align: "center",
      render: (color) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 4,
              backgroundColor: color,
              border: "1px solid #d9d9d9",
            }}
          ></div>
          <span>{color}</span>
        </div>
      ),
    },
    {
      title: "Icon Colour",
      dataIndex: "icon_colour",
      key: "icon_colour",
      align: "center",
      render: (color) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 4,
              backgroundColor: color,
              border: "1px solid #d9d9d9",
            }}
          ></div>
          <span>{color}</span>
        </div>
      ),
    },
    {
      title: "Content Type",
      dataIndex: "content_type",
      key: "content_type",
      render: (text) => (
        <span style={{ textTransform: "capitalize" }}>{text}</span>
      ),
    },
    {
      title: "Banner Name",
      dataIndex: "banner_name",
      key: "banner_name",
      render: (text) => text || "-",
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      render: (text) => DateFormat(text),
      sorter: true,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      render: (isActive, record) => (
        <Switch
          checked={isActive === 1}
          onChange={(checked) => handleBackdropStatusChange(record, checked)}
          size="small"
        />
      ),
    },
  ];

  // Add the backdrop status change handler
  const handleBackdropStatusChange = (record, checked) => {
    const statusText = checked ? "activate" : "deactivate";

    Modal.confirm({
      title: `Confirm Backdrop Status Change`,
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to ${statusText} the backdrop "${record.name}"?`,
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        setLoading(true);
        try {
          const values = {
            backdrop_id: record.id,
            is_active: checked ? 1 : 0,
          };

          const res = await saveApi(values, "", "updateBackdrop", "dashboard/");

          if (res) {
            successMessage(`Backdrop ${statusText}d successfully`);
            getBackdrops(backdropPayload);
          } else {
            errorMessage(`Failed to ${statusText} backdrop`);
            setLoading(false);
          }
        } catch (error) {
          console.error("Error updating backdrop status:", error);
          errorMessage(`Failed to ${statusText} backdrop`);
          setLoading(false);
        }
      },
    });
  };
  useEffect(() => {
    getStores();
  }, []);

  useEffect(() => {
    if (activeKey === "1") {
      getOffers(offerPayload);
    }
    else if (activeKey === "2") {
      getAds(adPayload);
    }
    else if (activeKey === "5") {
      getBackdrops(backdropPayload);
    }
  }, [storeId, activeKey]);

  const getStores = async () => {
    const res = await getApi({}, "getStores", "dashboard/");
    if (res.data && res.data.length) {
      const mappedStores = res.data.reduce((acc, store) => {
        acc[store.store_id] = store.name;
        return acc;
      }, {});
      setStoreMap(mappedStores);
    }
  };

  const handleTabChange = (key) => {
    setActiveKey(key);
    if (key === "1") {
      getOffers(offerPayload);
    } else if (key === "2") {
      getAds(adPayload);
    } else if (key === "3") {
      getTags(tagPayload);
    } else if (key === "5") {
      getBackdrops(backdropPayload);
    } else if (key === "6") {
      if (offerList.length === 0) {
        getOffers();
      }
    }
  };

  const getOffers = async (payload = {}) => {
    setLoading(true);
    if (storeId) payload = { ...payload, store_id: storeId };
    else delete payload.store_id;

    const res = await getApi(payload, "getOffer");
    if (res.data) {
      setOfferList(res.data);
      setOfferTotal(res.meta?.total);
      setOfferPayload(payload);
    }
    setLoading(false);
  };

  const saveOfferHandler = async (values) => {
    const selectedStoreId = storeId || values.store_id;
    if (selectedStoreId) values = { ...values, store_id: selectedStoreId };

    setOfferPopup(false);
    setLoading(true);
    let apiName = "createOffer";
    let msg = "Added Offer";
    if (offerSelected.id) {
      apiName = "updateOffer";
      msg = "Updated Offer";
      values = { ...values, offer_id: offerSelected.id };
    }
    const res = await saveApi(values, msg, apiName);
    if (res) {
      getOffers(offerPayload);
    } else {
      setLoading(false);
    }
  };

  async function getTags(payload = {}) {
    setLoading(true);
    const updatedPayload = { ...payload, type: "admin" };
    const res = await getApi(updatedPayload, "getTag");
    if (res.data) {
      setTagList([...res.data].sort((a, b) => a.tag_id - b.tag_id));
      setTagTotal(res.meta.total);
      setTagPayload(payload);
    }
    setLoading(false);
  }

  const saveTagHandler = async (values) => {
    setTagPopup(false);
    setLoading(true);
    let apiName = "createTag";
    let msg = "Added Tag";
    if (tagSelected.tag_id) {
      apiName = "updateTag";
      msg = "Updated Tag";
      values = { ...values, tag_id: tagSelected.tag_id };
    }
    const res = await saveApi(values, msg, apiName);
    if (res) {
      getTags(tagPayload);
    } else {
      setLoading(false);
    }
  };

  async function getAds(payload = {}) {
    setLoading(true);

    if (storeId) payload = { ...payload, store_id: storeId };
    else delete payload.store_id;

    payload = { ...payload, type: "admin" };

    const res = await getApi(payload, "getBanner");

    if (res.data) {
      setAdList(res.data);
      setAdTotal(res.meta?.total);
      setAdPayload(payload);
    }

    setLoading(false);
  }
  const saveAdHandler = async (values) => {
    const selectedStoreId = storeId || values.store_id;
    if (selectedStoreId) values = { ...values, store_id: selectedStoreId };

    setAdPopup(false);
    setLoading(true);

    let apiName = "createBanner";
    let msg = "Created Banner";
    let bannerId = null;

    if (adSelected.banner_id) {
      apiName = "updateBanner";
      msg = "Updated Banner";
      // Pass all banner details for update
      values = {
        ...values,
        banner_id: adSelected.banner_id,
        // Include all existing banner details
        name: values.name || adSelected.name,
        type: values.type || adSelected.type,
        valid_from: values.valid_from || adSelected.valid_from,
        valid_till: values.valid_till || adSelected.valid_till,
        // Include type-specific fields based on banner type
        ...(adSelected.type === "category" && {
          category_id: values.category_id || adSelected.category_id,
        }),
        ...(adSelected.type === "sub_category" && {
          sub_category_id: values.sub_category_id || adSelected.sub_category_id,
          category_id: values.category_id || adSelected.category_id,
        }),
        ...(adSelected.type === "product" && {
          product_variant_id:
            values.product_variant_id || adSelected.product_variant_id,
        }),
        ...(adSelected.type === "offer" && {
          offer_id: values.offer_id || adSelected.offer_id,
        }),
        ...(adSelected.type === "url" && { url: values.url || adSelected.url }),
      };
      bannerId = adSelected.banner_id;
    } else {
      values = { ...values, getId: true };
    }

    // Handle banner deletion if requested
    if (values.delete) {
      const res = await saveApi(
        { banner_image_id: adSelected.banner_image_id },
        "",
        "deleteBannerImage"
      );
      if (res) {
        console.log(res);
      } else {
        setLoading(false);
        return;
      }
    }

    try {
      // Step 1: Create/Update banner
      const bannerRes = await saveApi(values, "", apiName); // Don't show success message yet

      console.log("Banner response:", bannerRes); // Debug log

      if (bannerRes) {
        // Get the banner ID - handle both object and direct ID responses
        if (adSelected.banner_id) {
          bannerId = adSelected.banner_id;
        } else {
          // For new banners, extract ID from response
          if (typeof bannerRes === "object") {
            // Try different possible properties for the ID
            bannerId =
              bannerRes.data?.banner_id ||
              bannerRes.data?.id ||
              bannerRes.banner_id ||
              bannerRes.id ||
              bannerRes.data;
          } else {
            bannerId = bannerRes;
          }
        }

        console.log("Extracted Banner ID:", bannerId); // Debug log

        // Step 2: Upload image if provided and we have a valid banner ID
        if (
          values.file &&
          bannerId &&
          bannerId !== null &&
          bannerId !== undefined
        ) {
          const fmData = new FormData();
          const headers = { "content-type": "multipart/form-data" };
          fmData.append("banner_id", bannerId.toString()); // Ensure it's a string
          fmData.append("banner_img", values.file);

          console.log("Uploading image for banner ID:", bannerId); // Debug log

          const uploadRes = await getApi(
            fmData,
            "uploadBannerImage",
            "ecomm/",
            headers
          );

          console.log("Upload response:", uploadRes); // Debug log

          if (uploadRes && uploadRes.data) {
            // Show success message only after both banner and image are saved
            if (apiName === "createBanner") {
              successMessage("Banner created successfully with image");
            } else {
              successMessage("Banner updated successfully with image");
            }
            getAds(adPayload);
          } else {
            // If image upload fails, still show partial success
            errorMessage(
              "Banner saved but image upload failed. Please try uploading the image again."
            );
            getAds(adPayload);
          }
        } else if (values.file && !bannerId) {
          // If we have a file but no valid banner ID
          errorMessage(
            "Banner created but couldn't upload image - invalid banner ID"
          );
          console.error("Invalid banner ID for image upload:", bannerId);
          getAds(adPayload);
        } else {
          // No image to upload, just show banner success message
          if (apiName === "createBanner") {
            successMessage("Banner created successfully");
          } else {
            successMessage("Banner updated successfully");
          }
          getAds(adPayload);
        }
      } else {
        errorMessage("Failed to save banner");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error in banner creation/update:", error);
      errorMessage("Failed to save banner. Please try again.");
      setLoading(false);
    }
  };

  // Backdrop handlers
  async function getBackdrops(payload = {}) {
    setLoading(true);
    let updatedPayload = { ...payload, page: 0, size: 100 };
    if (storeId) updatedPayload = { ...updatedPayload, store_id: storeId };
    else delete updatedPayload.store_id;

    const res = await getApi(updatedPayload, "getBackdrop");
    if (res.data) {
      setBackdropList(res.data);
      setBackdropTotal(res.meta?.total || 0);
      setBackdropPayload(updatedPayload);
    }
    setLoading(false);
  }

  const saveBackdropHandler = async (values) => {
    const selectedStoreId = storeId || values.store_id;
    if (selectedStoreId) values = { ...values, store_id: selectedStoreId };

    setBackdropPopup(false);
    setLoading(true);

    let apiName = "createBackdrop";
    let msg = "Created Backdrop";
    let backdropId = null;

    if (backdropSelected.id) {
      apiName = "updateBackdrop";
      msg = "Updated Backdrop";
      values = {
        ...values,
        backdrop_id: backdropSelected.id,
      };
      backdropId = backdropSelected.id;
    } else {
      values = { ...values, getId: true };
    }

    // Handle backdrop image deletion if requested
    if (values.delete) {
      const res = await saveApi(
        { backdrop_id: backdropSelected.id },
        "",
        "deleteBackdropImage",
        "dashboard/"
      );
      if (res) {
        console.log(res);
      } else {
        setLoading(false);
        return;
      }
    }

    try {
      // Step 1: Create/Update backdrop
      const backdropRes = await saveApi(values, "", apiName, "dashboard/");

      console.log("Backdrop response:", backdropRes);

      if (backdropRes) {
        // Get the backdrop ID
        if (backdropSelected.id) {
          backdropId = backdropSelected.id;
        } else {
          // For new backdrops, extract ID from response
          if (typeof backdropRes === "object") {
            backdropId =
              backdropRes.data?.backdrop_id ||
              backdropRes.data?.id ||
              backdropRes.backdrop_id ||
              backdropRes.id ||
              backdropRes.data;
          } else {
            backdropId = backdropRes;
          }
        }

        console.log("Extracted Backdrop ID:", backdropId);

        // Step 2: Upload image if provided and we have a valid backdrop ID
        if (
          values.file &&
          backdropId &&
          backdropId !== null &&
          backdropId !== undefined
        ) {
          const fmData = new FormData();
          const headers = { "content-type": "multipart/form-data" };
          fmData.append("backdrop_id", backdropId.toString());
          fmData.append("backdrop_img", values.file);

          console.log("Uploading image for backdrop ID:", backdropId);

          const uploadRes = await getApi(
            fmData,
            "uploadBackdropImage",
            "dashboard/",
            headers
          );

          console.log("Upload response:", uploadRes);

          if (uploadRes && uploadRes.data) {
            successMessage(
              apiName === "createBackdrop"
                ? "Backdrop created successfully with image"
                : "Backdrop updated successfully with image"
            );
            getBackdrops(backdropPayload);
          } else {
            errorMessage(
              "Backdrop saved but image upload failed. Please try uploading the image again."
            );
            getBackdrops(backdropPayload);
          }
        } else if (values.file && !backdropId) {
          errorMessage(
            "Backdrop created but couldn't upload image - invalid backdrop ID"
          );
          console.error("Invalid backdrop ID for image upload:", backdropId);
          getBackdrops(backdropPayload);
        } else {
          successMessage(
            apiName === "createBackdrop"
              ? "Backdrop created successfully"
              : "Backdrop updated successfully"
          );
          getBackdrops(backdropPayload);
        }
      } else {
        errorMessage("Failed to save backdrop");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error in backdrop creation/update:", error);
      errorMessage("Failed to save backdrop. Please try again.");
      setLoading(false);
    }
  };

  // Offer Assign handler
  const assignOfferHandler = (values) => {
    const selectedOffer = offerList.find((o) => o.id === values.offer_id);
    Modal.confirm({
      title: "Confirm Offer Assignment",
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to assign this offer?</p>
          <p><strong>Mobile:</strong> {values.mobile}</p>
          <p><strong>Offer:</strong> {selectedOffer?.name || values.offer_id}</p>
        </div>
      ),
      okText: "Yes, Assign",
      cancelText: "Cancel",
      onOk: () => assignOfferApi(values),
    });
  };

  const assignOfferApi = async (values) => {
    setAssignLoading(true);
    try {
      const tokenString = localStorage.getItem("token");
      const userToken = JSON.parse(tokenString);
      const res = await axios.post(
        API_BASE_PATH + "mobile/assignOfferByMobile",
        { mobile: values.mobile, offer_id: values.offer_id },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (res.data.success) {
        message.success({ content: res.data.msg || "Offer assigned successfully", style: { marginTop: "8vh" } });
        assignForm.resetFields();
      } else {
        message.error({ content: res.data.msg || "Failed to assign offer", style: { marginTop: "8vh" } });
      }
    } catch (err) {
      message.error({ content: err?.response?.data?.msg || "Something went wrong", style: { marginTop: "8vh" } });
    }
    setAssignLoading(false);
  };

  // Fetch assigned offers list
  const getAssignedOffers = async () => {
    setAssignedOfferLoading(true);
    try {
      const tokenString = localStorage.getItem("token");
      const userToken = JSON.parse(tokenString);
      const res = await axios.post(
        API_BASE_PATH + "mobile/getUserOffers",
        { key: "JWT", secret: "RAPSAP" },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (res.data.success) {
        setAssignedOfferList(res.data.data || []);
      } else {
        errorMessage(res.data.msg || "Failed to fetch assigned offers");
      }
    } catch (err) {
      errorMessage(err?.response?.data?.msg || "Failed to fetch assigned offers");
    }
    setAssignedOfferLoading(false);
  };

  // Assigned offers table columns
  const assignedColumns = [
    {
      title: "User ID",
      dataIndex: "user_id",
      key: "user_id",
    },
    {
      title: "Offer Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Coupon Code",
      dataIndex: "coupon_code",
      key: "coupon_code",
    },
    {
      title: "Discount",
      dataIndex: "amount_discount",
      key: "amount_discount",
      render: (text) => (text ? `₹${text}` : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <span
          style={{
            textTransform: "capitalize",
            color:
              text === "redeemed"
                ? "#52c41a"
                : text === "assigned"
                  ? "#1890ff"
                  : "#faad14",
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Assigned At",
      dataIndex: "assigned_at",
      key: "assigned_at",
      render: (text) => (text ? DateFormat(text) : "-"),
    },
    {
      title: "Redeemed At",
      dataIndex: "redeemed_at",
      key: "redeemed_at",
      render: (text) => (text ? DateFormat(text) : "-"),
    },
    {
      title: "Valid Till",
      dataIndex: "valid_till",
      key: "valid_till",
      render: (text) => (text ? DateFormat(text, "DD MMM YY") : "-"),
    },
  ];

  return (
    <div className="card-container">
      <Tabs
        type="card"
        activeKey={activeKey}
        onChange={handleTabChange}
        destroyInactiveTabPane={true}
      >
        <Tabs.TabPane tab="Offers" key="1">
          {offerPopup && (
            <OfferPopup
              title={offerPopupTitle}
              visible={offerPopup}
              data={
                offerSelected.variant_id
                  ? {
                    ...offerSelected,
                    variant_name:
                      offerSelected.product_name +
                      " " +
                      offerSelected.variant_name,
                  }
                  : offerSelected
              }
              cancelHandler={() => setOfferPopup(false)}
              saveHandler={saveOfferHandler}
            />
          )}
          {offerSidebar && (
            <OfferSidebar
              editOfferHandler={() => {
                setOfferSidebar(false);
                setOfferPopup(true);
                setOfferPopupTitle("Edit Offer Details");
              }}
              onUploadDone={() => (
                setOfferSidebar(false), getOffers(offerPayload)
              )}
              data={offerSelected}
              visible={offerSidebar}
              onClose={() => setOfferSidebar(false)}
            />
          )}
          <CardContainer bordered={false} hoverable={false}>
            <TableContainer
              breadcrumbTitle="Offers Management"
              loading={loading}
              columns={offerColumns}
              data={offerList}
              total={offerTotal}
              dateRangePicker
              addButton={true}
              searchFor="Coupon Code, Name"
              addHandler={() => (
                setOfferSelected({}),
                setOfferPopupTitle("Add Offer"),
                setOfferPopup(true)
              )}
              rowClickHandler={(record) => (
                setOfferSelected(record), setOfferSidebar(true)
              )}
              functionsHandler={getOffers}
            />
          </CardContainer>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Banners" key="2">
          {adPopup && (
            <AdPopup
              visible
              title={
                adSelected.banner_id ? "Edit banner Details" : "Create a Banner"
              }
              cancelHandler={() => (setAdSelected({}), setAdPopup(false))}
              saveHandler={saveAdHandler}
              data={
                adSelected.type === "sub_category"
                  ? {
                    ...adSelected,
                    sub_category_id:
                      adSelected.sub_category_id +
                      ";" +
                      adSelected.category_id,
                  }
                  : adSelected.type === "product"
                    ? {
                      ...adSelected,
                      pv_name:
                        adSelected.product_name +
                        " - " +
                        adSelected.variant_name,
                    }
                    : adSelected
              }
            />
          )}
          {adSidebar && (
            <AdSidebar
              editAdHandler={() => {
                setAdSidebar(false);
                setAdPopup(true);
              }}
              onUploadDone={() => (setAdSidebar(false), getAds(adPayload))}
              data={adSelected}
              visible
              onClose={() => (setAdSelected({}), setAdSidebar(false))}
            />
          )}
          <CardContainer bordered={false} hoverable={false}>
            <TableContainer
              breadcrumbTitle="Banners Management"
              loading={loading}
              columns={adColumns}
              data={adList}
              total={adTotal}
              dateRangePicker
              searchFor="Banner Name"
              addButton={true}
              addHandler={() => (setAdSelected({}), setAdPopup(true))}
              rowClickHandler={(record) => (
                setAdSelected(record), setAdSidebar(true)
              )}
              functionsHandler={getAds}
            />
          </CardContainer>
        </Tabs.TabPane>
        <Tabs.TabPane tab="Tags" key="3">
          {tagPopup && (
            <TagPopup
              title={tagPopupTitle}
              visible={tagPopup}
              cancelHandler={() => setTagPopup(false)}
              data={tagSelected}
              saveHandler={saveTagHandler}
            />
          )}
          <CardContainer bordered={false} hoverable={false}>
            <TableContainer
              breadcrumbTitle="Tags Management"
              loading={loading}
              columns={tagColumns}
              data={tagList}
              total={tagTotal}
              addButton={storeId ? false : true}
              searchFor="Tag Name"
              functionsHandler={getTags}
              addHandler={() => (
                setTagPopup(true),
                setTagSelected({}),
                setTagPopupTitle("Add Tag")
              )}
              rowClickHandler={(record) =>
                storeId
                  ? null
                  : (setTagSelected(record),
                    setTagPopupTitle("Edit Tag Details"),
                    setTagPopup(true))
              }
              scrollable={false}
            />
          </CardContainer>
        </Tabs.TabPane>

        <Tabs.TabPane tab="TV Ads" key="4">
          <TVAdEditor />
        </Tabs.TabPane>

        <Tabs.TabPane tab="Backdrop" key="5">
          {backdropPopup && (
            <BackdropPopup
              visible
              title={
                backdropSelected.id ? "Edit Backdrop Details" : "Create a Backdrop"
              }
              cancelHandler={() => (setBackdropSelected({}), setBackdropPopup(false))}
              saveHandler={saveBackdropHandler}
              data={backdropSelected}
            />
          )}
          {backdropSidebar && (
            <BackdropSidebar
              editBackdropHandler={() => {
                setBackdropSidebar(false);
                setBackdropPopup(true);
              }}
              onUploadDone={() => (setBackdropSidebar(false), getBackdrops(backdropPayload))}
              data={backdropSelected}
              visible
              onClose={() => (setBackdropSelected({}), setBackdropSidebar(false))}
            />
          )}
          <CardContainer bordered={false} hoverable={false}>
            <TableContainer
              breadcrumbTitle="Backdrop Management"
              loading={loading}
              columns={backdropColumns}
              data={backdropList}
              total={backdropTotal}
              dateRangePicker
              searchFor="Backdrop Name"
              addButton={true}
              addHandler={() => (setBackdropSelected({}), setBackdropPopup(true))}
              rowClickHandler={(record) => (
                setBackdropSelected(record), setBackdropSidebar(true)
              )}
              functionsHandler={getBackdrops}
            />
          </CardContainer>

        </Tabs.TabPane>

        <Tabs.TabPane tab="Offer Assign" key="6">
          {/* Detail Modal */}
          <Modal
            title="Assigned Offer Details"
            visible={!!selectedAssignedOffer}
            onCancel={() => setSelectedAssignedOffer(null)}
            footer={[
              <Button key="close" onClick={() => setSelectedAssignedOffer(null)}>
                Close
              </Button>,
            ]}
          >
            {selectedAssignedOffer && (
              <div>
                <p><strong>Offer Name:</strong> {selectedAssignedOffer.name}</p>
                <p><strong>Coupon Code:</strong> {selectedAssignedOffer.coupon_code}</p>
                <p><strong>User ID:</strong> {selectedAssignedOffer.user_id}</p>
                <p><strong>Discount:</strong> ₹{selectedAssignedOffer.amount_discount}</p>
                <p><strong>Min Amount:</strong> ₹{selectedAssignedOffer.min_amount}</p>
                <p><strong>Status:</strong> {selectedAssignedOffer.status}</p>
                <p><strong>Assigned At:</strong> {selectedAssignedOffer.assigned_at ? DateFormat(selectedAssignedOffer.assigned_at) : "-"}</p>
                <p><strong>Activated At:</strong> {selectedAssignedOffer.activated_at ? DateFormat(selectedAssignedOffer.activated_at) : "-"}</p>
                <p><strong>Redeemed At:</strong> {selectedAssignedOffer.redeemed_at ? DateFormat(selectedAssignedOffer.redeemed_at) : "-"}</p>
                <p><strong>Valid Till:</strong> {selectedAssignedOffer.valid_till ? DateFormat(selectedAssignedOffer.valid_till, "DD MMM YY") : "-"}</p>
                {selectedAssignedOffer.image_url && (
                  <div style={{ marginTop: 12 }}>
                    <strong>Image:</strong>
                    <br />
                    <Image
                      src={selectedAssignedOffer.image_url}
                      width={200}
                      style={{ marginTop: 8, borderRadius: 4 }}
                    />
                  </div>
                )}
              </div>
            )}
          </Modal>

          <CardContainer bordered={false} hoverable={false}>
            <div style={{ padding: "20px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 24,
                }}
              >
                <h3 style={{ margin: 0 }}>
                  {showAssignedTable ? "Assigned Offers" : "Assign Offer to User"}
                </h3>
                <Button
                  type={showAssignedTable ? "primary" : "default"}
                  onClick={() => {
                    if (!showAssignedTable) {
                      getAssignedOffers();
                    }
                    setShowAssignedTable(!showAssignedTable);
                  }}
                >
                  {showAssignedTable ? "Back to Assign" : "View Assigned"}
                </Button>
              </div>

              {showAssignedTable ? (
                <Table
                  columns={assignedColumns}
                  dataSource={assignedOfferList}
                  rowKey="user_offer_id"
                  loading={assignedOfferLoading}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  bordered
                  onRow={(record) => ({
                    onClick: () => setSelectedAssignedOffer(record),
                    style: { cursor: "pointer" },
                  })}
                />
              ) : (
                <Form
                  form={assignForm}
                  layout="vertical"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  onFinish={assignOfferHandler}
                  style={{ width: 450 }}
                >
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: "block", fontWeight: 500, marginBottom: 8 }}>
                      Mobile Number <span style={{ color: "#ff4d4f" }}>*</span>
                    </label>
                    <Form.Item
                      name="mobile"
                      noStyle
                      rules={[
                        { required: true, message: "Please enter mobile number" },
                        {
                          pattern: /^[0-9]{10}$/,
                          message: "Please enter a valid 10-digit mobile number",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter 10-digit mobile number"
                        maxLength={10}
                        size="large"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: "block", fontWeight: 500, marginBottom: 8 }}>
                      Offer <span style={{ color: "#ff4d4f" }}>*</span>
                    </label>
                    <Form.Item
                      name="offer_id"
                      noStyle
                      rules={[
                        { required: true, message: "Please select an offer" },
                      ]}
                    >
                      <Select
                        placeholder="Select an offer"
                        showSearch
                        optionFilterProp="children"
                        size="large"
                        style={{ width: "100%" }}
                      >
                        {offerList.map((offer) => (
                          <Select.Option key={offer.id} value={offer.id}>
                            {offer.name} ({offer.coupon_code})
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={assignLoading}
                    size="large"
                  >
                    Assign Offer
                  </Button>
                </Form>
              )}
            </div>
          </CardContainer>
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
}
